<template>
  <div>
    <v-progress-linear v-if="loading" indeterminate color="primary" />
    <v-toolbar flat dense>
      <v-btn v-if="allowCreateCard" small color="primary" @click="createCard">
        <v-icon left small>mdi-plus</v-icon>{{ addButtonLabel }}
      </v-btn>
    </v-toolbar>
    <v-row>
      <v-col v-if="options.showUnassigned" cols="12" md="3">
        <v-card outlined>
          <v-card-title>À répartir</v-card-title>
          <v-card-text>
            <div v-for="subject in availableSubjects" :key="`subject-${subjectId(subject)}`" draggable="true" class="repartition-subject mb-2" @dragstart="startDrag(subject)" @dragend="endDrag">
              <div class="repartition-row-actions">
                <span v-for="action in subject.actions || []" :key="action.id" class="d-inline-flex align-center">
                <v-btn icon x-small :title="action.name" @click.stop="runAction(subject, action)">
                  <v-icon small>{{ action.icon || 'mdi-play' }}</v-icon>
                </v-btn>
                <rights-indicator :checks="actionInspectionChecks(action)" :label="action.name" />
                </span>
              </div>
              <CPage :pageTemplate="templateFor('subject',subject)" :customparams="subjectContext(subject)" :page="page" :modal="modal" :modalKey="modalKey" :form="false" :mod="false" />
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col>
        <div class="repartition-cards">
          <div
            v-for="card in cards"
            :key="`card-${card.id}`"
            class="repartition-card"
            :class="[`shape-${appearanceFor(card).shape}`, { 'repartition-card--drop-target': String(hoveredCardId) === String(cardId(card)) }]"
            :style="cardStyle(card)"
            @dragenter.prevent="enterCard($event, card)"
            @dragover.prevent
            @dragleave="leaveCard($event, card)"
            @drop.prevent="dropOn($event, card)"
          >
            <div class="repartition-card__actions">
              <span v-for="action in card.actions || []" :key="action.id" class="d-inline-flex align-center">
              <v-btn icon small :title="action.name" @click.stop="runAction(card, action)">
                <v-icon small>{{ action.icon || 'mdi-play' }}</v-icon>
              </v-btn>
              <rights-indicator :checks="actionInspectionChecks(action)" :label="action.name" />
              </span>
            </div>
            <CPage :pageTemplate="templateFor('card',card)" :customparams="cardContext(card)" :page="page" :modal="modal" :modalKey="modalKey" :form="false" :mod="false" />
            <div
              v-for="allocation in allocationsFor(card)"
              :key="`allocation-${allocationId(allocation)}`"
              class="repartition-allocation"
              draggable="true"
              @dragstart.stop="startAllocationDrag(allocation)"
              @dragend="endDrag"
            >
              <CPage :pageTemplate="templateFor('allocation',allocationContext(allocation))" :customparams="allocationContext(allocation)" :page="page" :modal="modal" :modalKey="modalKey" :form="false" :mod="false" />
              <div class="repartition-row-actions">
                <span v-for="action in allocation.actions || []" :key="action.id" class="d-inline-flex align-center">
                <v-btn icon x-small :title="action.name" @click.stop="runAction(allocationContext(allocation), action)">
                  <v-icon small>{{ action.icon || 'mdi-play' }}</v-icon>
                </v-btn>
                <rights-indicator :checks="actionInspectionChecks(action)" :label="action.name" />
                </span>
              </div>
            </div>
          </div>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { client } from '../mixins'
import {
  envelopeFromDataTransfer,
  hasEngineDragType,
  mappedDropFields,
  matchingConnection,
} from '../shared/dropReceiver'

export default {
  name: 'RepartitionClient',
  mixins: [client],
  data: () => ({
    loading: false, cards: [], subjects: [], allocations: [], config: {},
    display: { card: [], subject: [], allocation: [] }, options: { showUnassigned: true },
    mode: 'splittable', dragged: null, draggedAllocation: null, hoveredCardId: null, querys: {}, searchmodule: [],
    // Indique que la modale courante a été ouverte depuis ce module.
    fromThisModule: false,
    observedModalOpen: false,
    // Module draggablelist à rafraîchir après la fermeture de la page ouverte
    // par un drop externe. L'ID vient de l'enveloppe validée par la connexion.
    pendingSourceModuleReload: null,
  }),
  computed: {
    // La recherche d'une modale est portée par la fenêtre qui l'héberge.
    currentSearchQuery() {
      const current = this.$store.getters.modalShowParams({ type: 'current', val: this.modalKey })
      const parent = current && current.parent
        ? this.$store.getters.modalShowParams({ type: 'current', val: current.parent })
        : null
      // La query courante fait foi, sans réintroduire d'anciens critères
      // depuis une copie customquerys après leur suppression.
      return parent ? parent.query || {} : this.$route.query || {}
    },
    allowCreateCard() { return !!(this.config.cards && this.config.cards.allowCreate && this.config.cards.newpage) },
    addButtonLabel() { return (this.config.cards && this.config.cards.addButtonLabel) || 'Ajouter un encart' },
    availableSubjects() { return this.subjects.filter(subject => this.remaining(subject) === null || this.remaining(subject) > 0) },
    // État Vuex de la modale enfant rattachée à la page du module.
    callBackOnCloseModal() {
      return this.$store.getters.modalShowParams({ type: 'current', val: this.modalKey })
    },
  },
  watch: {
    currentSearchQuery: {
      deep: true,
      handler(value) {
        const previous = this.querys || {}
        const keys = new Set([...Object.keys(value || {}), ...Object.keys(previous)])
        const changed = [...keys].some(key => this.searchmodule.some(id => key.startsWith(id + '_'))
          && JSON.stringify((value || {})[key]) !== JSON.stringify(previous[key]))
        if (!changed) return
        this.querys = { ...value }
        this.callAPI()
      },
    },
    callBackOnCloseModal: {
      deep: true,
      handler(modalState) {
        if (modalState && modalState.show === true) {
          this.observedModalOpen = true
          return
        }
        // Même principe que TableModule : recharge après fermeture de la page
        // d'ajout ou d'édition, mais uniquement si elle vient de ce module.
        if (modalState && modalState.show === false && (this.fromThisModule || (this.options.reloadOnModalClose && this.observedModalOpen))) {
          this.observedModalOpen = false
          this.fromThisModule = false
          const sourceModuleId = this.pendingSourceModuleReload
          this.pendingSourceModuleReload = null
          this.callAPI().then(() => {
            this.reloadRelatedModules(sourceModuleId)
          })
        }
      },
    },
  },
  methods: {
    callAPI(action = 'load', payload = {}) {
      this.loading = true
      return this.API({ type: 'post', url: 'displaymodule', params: { id: this.idmodule, params: this.params, querys: this.querys, action, payload } })
        .then(response => {
          const data = response.data || response
          if (data.success !== false) {
            this.cards = data.cards || []
            this.subjects = data.subjects || []
            this.allocations = data.allocations || []
            this.display = data.display || this.display
            this.options = data.options || this.options
            this.mode = data.mode || this.mode
            this.config = data.config || {}
            this.searchmodule = Array.isArray(data.searchmodule) ? data.searchmodule : []
          }
          return data
        }).finally(() => { this.loading = false })
    },
    openPage(name, typeform, replace) {
      if (!name) return
      this.goTo({ modalKey: this.modalKey, name, typeform, type: 'modal', replace, paramDialog: { width: 1000 } })
      this.fromThisModule = true
    },
    createCard() {
      const mappings = (this.config.cards.createParams || []).map(mapping => ({
        ...mapping,
        value: typeof mapping.value === 'string'
          ? this.replaceBy({ text: mapping.value, page: this.modalKey, line: this.params })
          : mapping.value,
      }))
      const fields = mappedDropFields({ mappings }, {}, {})
      this.openPage(this.config.cards.newpage, 'new', { id: 'new', idparent: this.params.id, ...fields })
    },
    // Réactualise les listes sources et les modules associés après une écriture.
    reloadRelatedModules(preferredSourceId = null) {
      const connections = (this.config.dropReceiver || {}).connections || []
      const ids = [preferredSourceId, ...(this.options.reloadModules || [])]
      connections.forEach(connection => {
        if (connection.enabled === false) return
        const sources = connection.sourceModules || []
        ids.push(...(Array.isArray(sources) ? sources : String(sources).split(',')))
      })
      new Set(ids.map(Number).filter(id => Number.isInteger(id) && id > 0 && id !== Number(this.idmodule)))
        .forEach(id => this.$store.dispatch('addReloadModule', { modules: id }))
    },
    runAction(row, action) {
      if (action.target === 'modal') this.fromThisModule = true
      this.clickActions({
        args: row, action, idmodule: this.idmodule, page: this.page, modalKey: this.modalKey,
        success: data => {
          if (data.message) this.$toast.success(data.message)
          if (action.typelink === 'api') this.callAPI().then(() => this.reloadRelatedModules())
        },
      })
    },
    startDrag(subject) {
      this.draggedAllocation = null
      this.dragged = subject
    },
    startAllocationDrag(allocation) {
      this.dragged = null
      this.draggedAllocation = allocation
    },
    enterCard(event, card) {
      if (this.dragged || this.draggedAllocation) {
        this.hoveredCardId = this.cardId(card)
        return
      }
      const receiver = this.config.dropReceiver || { enabled: false, connections: [] }
      if (!receiver.enabled || !hasEngineDragType(event.dataTransfer)) return
      this.hoveredCardId = this.cardId(card)
    },
    leaveCard(event, card) {
      // dragleave se déclenche aussi en passant sur un enfant de l'encart.
      // On retire l'effet seulement lorsque le pointeur sort réellement de l'encart.
      if (event.currentTarget.contains(event.relatedTarget)) return
      if (String(this.hoveredCardId) === String(this.cardId(card))) this.hoveredCardId = null
    },
    endDrag() {
      this.hoveredCardId = null
      this.dragged = null
      this.draggedAllocation = null
    },
    dropOn(event, card) {
      this.hoveredCardId = null

      // Un module draggablelist transmet son enveloppe par DataTransfer. Le
      // module Répartition conserve en parallèle son drag interne historique.
      const externalEnvelope = envelopeFromDataTransfer(event && event.dataTransfer)
      if (externalEnvelope) {
        this.receiveExternalDrop(externalEnvelope, card)
        return
      }

      // Une affectation existante est déplacée directement vers le nouvel encart.
      // Les autres valeurs de la ligne (quantité, palettes, commentaire) restent inchangées.
      if (this.draggedAllocation) {
        const allocation = this.draggedAllocation
        this.draggedAllocation = null
        if (String(this.allocationCardId(allocation)) === String(this.cardId(card))) return
        this.callAPI('moveAllocation', {
          id: this.allocationId(allocation),
          card_id: this.cardId(card),
          subject_id: this.allocationSubjectId(allocation),
          fields: {},
        })
        return
      }

      // Le backend insère une répartition ou met à jour l'élément selon le mode configuré.
      if (!this.dragged || !this.config.allocation) return
      const remaining = this.remaining(this.dragged)
      const payload = {
        card_id: this.cardId(card), subject_id: this.subjectId(this.dragged),
        quantity: remaining === null ? 0 : remaining, line: this.dragged,
      }
      const dropAction = (this.config.operations && this.config.operations.dropAction) || 'insert'
      if (dropAction === 'insert') {
        if (!this.config.allocation.newpage) {
          this.$toast.error('Aucune page de création n’est configurée pour la répartition')
          return
        }
        this.callAPI('prepareDrop', payload).then(data => {
          if (!data || data.success === false) return
          this.openPage(this.config.allocation.newpage, 'new', {
            id: 'new', idparent: this.cardId(card), ...(data.dropFields || {}),
          })
        })
      } else {
        this.callAPI('drop', payload)
      }
      this.dragged = null
    },
    receiveExternalDrop(envelope, card) {
      const receiver = this.config.dropReceiver || { enabled: false, connections: [] }
      const connection = matchingConnection(receiver, envelope)
      if (!connection) {
        this.$toast.warning('Cet élément ne peut pas être déposé dans cet encart')
        return
      }

      const fields = mappedDropFields(connection, envelope, { cardId: this.cardId(card) })
      const page = connection.page || (this.config.allocation && this.config.allocation.newpage)
      if (!page) {
        this.$toast.error('Aucune page n’est configurée pour ce dépôt')
        return
      }

      this.pendingSourceModuleReload = envelope.sourceModuleId
      const isUpdate = connection.mode === 'update' && fields.id !== undefined && fields.id !== ''
      this.openPage(page, isUpdate ? 'edit' : 'new', {
        id: isUpdate ? fields.id : 'new',
        idparent: this.cardId(card),
        ...fields,
      })
    },
    templateFor(kind, row) {
      const variants = this.display[kind] || []
      return JSON.parse(JSON.stringify((variants.find(v => v.key === (row.displayKey || 'default')) || variants.find(v => v.key === 'default') || {}).construct || []))
    },
    appearanceFor(card) {
      const variants = this.display.card || []
      return (variants.find(v => v.key === (card.displayKey || 'default')) || variants[0] || {}).appearance || { shape: 'rectangle' }
    },
    cardStyle(card) {
      const appearance = this.appearanceFor(card)
      return { minHeight: `${appearance.minHeight || 220}px`, width: `${appearance.width || 360}px`, backgroundColor: appearance.backgroundColor || '#fff', borderColor: appearance.borderColor || '#607d8b', borderWidth: `${appearance.borderWidth || 2}px`, borderRadius: `${appearance.borderRadius || 8}px` }
    },
    valueByAlias(row, alias, fallback) { return (alias && row[alias] !== undefined) ? row[alias] : fallback },
    subjectId(subject) { return this.valueByAlias(subject, this.config.subjects && this.config.subjects.idAlias, subject.id) },
    cardId(card) { return this.valueByAlias(card, this.config.cards && this.config.cards.idAlias, card.id) },
    allocationId(allocation) { return this.valueByAlias(allocation, this.config.allocation && this.config.allocation.idAlias, allocation.id) },
    allocationCardId(allocation) { return this.valueByAlias(allocation, this.config.allocation && this.config.allocation.cardAlias, allocation.card_id) },
    allocationSubjectId(allocation) { return this.valueByAlias(allocation, this.config.allocation && this.config.allocation.subjectAlias, allocation.subject_id) },
    allocationQuantity(allocation) { return Number(this.valueByAlias(allocation, this.config.allocation && this.config.allocation.quantityAlias, 0)) },
    allocationsFor(card) { return this.allocations.filter(allocation => String(this.allocationCardId(allocation)) === String(this.cardId(card))) },
    allocationContext(allocation) {
      const subject = this.subjects.find(item => String(this.subjectId(item)) === String(this.allocationSubjectId(allocation))) || {}
      const context = {
        ...subject,
        ...allocation,
        cmd_cls_lines: { ...subject, subject_remaining: this.remaining(subject) },
        cmd_cls_containers_lines: { ...allocation },
      }
      if (this.config.subjects && this.config.subjects.table) context[this.config.subjects.table] = { ...subject, subject_remaining: this.remaining(subject) }
      if (this.config.allocation && this.config.allocation.table) context[this.config.allocation.table] = { ...allocation }
      return context
    },
    subjectContext(subject) {
      const context = { ...subject, subject_remaining: this.remaining(subject) }
      const result = { ...context, cmd_cls_lines: context }
      if (this.config.subjects && this.config.subjects.table) result[this.config.subjects.table] = context
      return result
    },
    cardContext(card) {
      const context = { ...card, cmd_cls_containers: { ...card } }
      if (this.config.cards && this.config.cards.table) context[this.config.cards.table] = { ...card }
      return context
    },
    remaining(subject) {
      const total = Number(this.valueByAlias(subject, this.config.subjects && this.config.subjects.quantityAlias, NaN))
      if (!Number.isFinite(total)) return null
      return total - this.allocations.filter(allocation => String(this.allocationSubjectId(allocation)) === String(this.subjectId(subject))).reduce((sum, allocation) => sum + this.allocationQuantity(allocation), 0)
    },
  },
  mounted() {
    const context = this.$store.getters.specificPage(this.modalKey) || {}
    this.params = this.customparams || context.params || {}
    this.querys = { ...this.currentSearchQuery }
    this.callAPI()
  },
}
</script>

<style scoped>
.repartition-cards{display:flex;flex-wrap:wrap;gap:16px;align-items:flex-start}.repartition-card{position:relative;border-style:solid;padding:12px;overflow:hidden;transition:box-shadow .15s ease,border-color .15s ease,transform .15s ease}.repartition-card--drop-target{box-shadow:inset 0 0 0 9999px rgba(80,80,80,.14),0 0 0 2px rgba(80,80,80,.35);transform:translateY(-2px)}.repartition-card__actions{position:absolute;top:4px;right:4px;z-index:2;display:flex;background:rgba(255,255,255,.8);border-radius:18px}.repartition-subject,.repartition-allocation{position:relative;border:1px solid #ddd;border-radius:6px;padding:8px;background:#fff;cursor:grab}.repartition-allocation{display:flex;align-items:center;justify-content:space-between;margin-top:8px;cursor:grab}.repartition-row-actions{display:flex;justify-content:flex-end;align-items:center;gap:2px}.shape-container{background-image:repeating-linear-gradient(90deg,transparent 0,transparent 28px,rgba(0,0,0,.06) 29px,rgba(0,0,0,.06) 31px)}.shape-tank{border-radius:50%/10%!important}.shape-horizontal-tank{border-radius:12%/50%!important}.shape-circle{border-radius:50%!important;aspect-ratio:1/1}
</style>
